//char
//char
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { MASTER_API } from '../apiConfig';
import { getCharacteristicInputType, calculateToleranceRange } from '../ProcurementManagement/ProductManagement/ProductConfiguration';

// "Type of Packing" is rendered as a dropdown instead of a free-text input.
// The options used to be hardcoded here; they now come from Configuration
// ("Type of Packing" master, type "type-of-packing") so packing options can
// be managed centrally instead of edited in code.
const isTypeOfPacking = (characteristicName) =>
    (characteristicName || "").trim().toLowerCase() === "type of packing";

// Over/Under Tolerances get a checkbox beside the value input — but only
// when Product Configuration has this characteristic's "Tolerance Checkbox"
// turned on for this class. If it's off (or was never set) in Product
// Config, Enquiry just shows the plain value input, same as any other
// characteristic — checking either one links and auto-checks the other,
// since a Range is only ever calculated using both together.
const isToleranceChar = (characteristicName) => {
    const name = (characteristicName || "").trim().toLowerCase().replace(/\s+/g, "");
    return name === "overtolerance" || name === "undertolerance";
};

const showsToleranceCheckbox = (char) =>
    isToleranceChar(char.characteristic) && !!char.tolerance_checkbox;

// "No. of Drum/Coil" and "Length of the Drum/Coil" should only ever hold
// numbers, same as Over/Under Tolerance — restrict input to digits (and a
// single decimal point) as the user types, rather than validating on submit.
// This is a legacy fallback for classes where Product Configuration hasn't
// tagged the characteristic with "Text"/"Numeric" yet — see resolveInputType.
const isNumericOnlyChar = (characteristicName) => {
    const name = (characteristicName || "").trim().toLowerCase();
    return name === "no. of drum/coil"
        || name === "length of the drum/coil"
        || isToleranceChar(characteristicName);
};

// Resolves what a characteristic's value input should accept: Product
// Configuration's "Text"/"Numeric" tag on calculation_type wins when set
// (this is the single source of truth — configured once per class, applied
// wherever the characteristic is typed into); otherwise fall back to the
// legacy hardcoded numeric-name list above so existing classes that were
// never re-tagged keep working exactly as before.
const resolveInputType = (char) => {
    const configType = getCharacteristicInputType(char.calculation_type);
    if (configType) return configType;
    if (isNumericOnlyChar(char.characteristic)) return "numeric";
    return null;
};

// Strips anything that isn't a digit or decimal point, and collapses to at
// most one decimal point, so partial/invalid keystrokes are simply dropped.
const sanitizeNumeric = (raw) => {
    let value = (raw || "").replace(/[^0-9.]/g, "");
    const firstDot = value.indexOf(".");
    if (firstDot !== -1) {
        value = value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, "");
    }
    return value;
};

// Strips digits so a "Text" characteristic can't end up with numbers typed
// into it — mirrors sanitizeNumeric's "drop the invalid keystroke, keep the
// rest" behaviour instead of rejecting the whole change.
const sanitizeText = (raw) => (raw || "").replace(/\d/g, "");

// Gives Over/Under Tolerance and Type of Packing columns a stable minimum
// width so the table's auto layout can't squeeze them differently row to
// row (which is what was throwing the tolerance inputs out of alignment
// and forcing "Range" to wrap onto two lines).
const getColStyle = (characteristicName) => {
    const key = (characteristicName || "").trim().toLowerCase().replace(/\s+/g, "");
    if (key === "overtolerance" || key === "undertolerance") {
        return { minWidth: '140px' };
    }
    if (key === "typeofpacking") {
        return { minWidth: '170px' };
    }
    return {};
};

const CharacteristicsModal = ({ modalVisible, selectedCharacteristics, handleChange, handleToggleTolerance, handleClose, onAdd, onRemove, onSave, isEditing, title }) => {
    // Packing options now come from Configuration ("Type of Packing") instead
    // of being hardcoded — fetched once when the modal opens.
    const [packingOptions, setPackingOptions] = useState([]);

    useEffect(() => {
        if (!modalVisible) return;
        axios.get(`${MASTER_API}/configuration/dynamic/type-of-packing`)
            .then((res) => setPackingOptions(res.data || []))
            .catch((err) => console.error("Failed to fetch Type of Packing options:", err));
    }, [modalVisible]);

    if (!modalVisible) return null;

    // Show a single shared note when any Over/Under Tolerance checkbox is
    // checked, instead of repeating "Range is calculated using Length of
    // the Drum/Coil" under every row — that repetition was what pushed
    // Over Tolerance and Under Tolerance apart from each other.
    const showToleranceNote = (selectedCharacteristics || []).some(
        (c) => showsToleranceCheckbox(c) && !!c.tolerance_applied
    );

    return (
        <div style={styles.modalBackdrop}>
            <div style={styles.modal}>
                {/* Header */}
                <div style={styles.modalHeader}>
                    <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                       
                    </div>
                    {isEditing === false ? (
                        <button style={styles.iconBtn} onClick={handleClose}>✕</button>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={onAdd}
                            style={{ fontSize: '13px' }}
                        >
                            + Add
                        </button>
                    )}
                </div>

                {/* Body */}
                <div style={styles.modalBody}>
                    {showToleranceNote && (
                        <div style={styles.toleranceNote}>
                            Note: Range is calculated using Length of the Drum/Coil
                        </div>
                    )}
                    <div style={styles.tableContainer}>
                        <table className="table mb-0" style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead style={{ background: "#f8fafc" }}>
                                <tr>
                                    {selectedCharacteristics && selectedCharacteristics.length > 0 && (() => {
                                        const cols = [];
                                        const seen = {};
                                        selectedCharacteristics.forEach((char) => {
                                            const key = (char.characteristic || "").trim().toLowerCase();
                                            if (!seen[key]) { seen[key] = true; cols.push(char.characteristic); }
                                        });
                                        const lower = cols.map(c => (c || "").toLowerCase());
                                        const hasLength = lower.some(n => n.includes("length") && (n.includes("drum") || n.includes("coil")));
                                        const hasTolerance = lower.some(n => n.replace(/\s+/g, "") === "overtolerance" || n.replace(/\s+/g, "") === "undertolerance");
                                        const showRangeCol = hasLength && hasTolerance;
                                        return (
                                            <>
                                                {cols.map((name, i) => (
                                                    <th key={i} style={{ ...styles.erpTh, ...getColStyle(name) }}>{name}</th>
                                                ))}
                                                {showRangeCol && <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Range</th>}
                                                {isEditing !== false && onRemove && <th style={styles.erpTh}></th>}
                                            </>
                                        );
                                    })()}
                                </tr>
                            </thead>
                            <tbody>
                                {selectedCharacteristics && selectedCharacteristics.length > 0 ? (
                                    (() => {
                                        // Group entries by characteristic name (case-insensitive),
                                        // preserving first-appearance order. Each unique name
                                        // becomes one COLUMN; repeated "+ Add" clicks push extra
                                        // {characteristic, value} entries onto selectedCharacteristics
                                        // with the same name — the nth entry of every name is that
                                        // name's nth "+ Add" group, so occurrence index g across all
                                        // columns belongs together as ROW g (one Drum/Coil group per
                                        // row), instead of being crammed side-by-side inside one row.
                                        const columns = [];
                                        const colIndexByName = {};
                                        selectedCharacteristics.forEach((char, index) => {
                                            const key = (char.characteristic || "").trim().toLowerCase();
                                            if (colIndexByName[key] === undefined) {
                                                colIndexByName[key] = columns.length;
                                                columns.push({ characteristic: char.characteristic, entries: [{ char, index }] });
                                            } else {
                                                columns[colIndexByName[key]].entries.push({ char, index });
                                            }
                                        });

                                        const groupCount = Math.max(1, ...columns.map(c => c.entries.length));

                                        // Locate the specific columns Range needs. Matched the same
                                        // way enquiry.js's handleSaveCharModal finds them for the
                                        // overall Range, so a row's Range here uses identical logic,
                                        // just scoped to that one Drum/Coil group instead of all of them.
                                        const findCol = (matcher) => columns.find(
                                            (c) => matcher((c.characteristic || "").toLowerCase())
                                        );
                                        const lengthCol = findCol((n) => n.includes("length") && (n.includes("drum") || n.includes("coil")));
                                        const noOfDrumCol = findCol((n) => /no\.?\s*of\s*(drum|coil)/.test(n));
                                        const overCol = findCol((n) => n.replace(/\s+/g, "") === "overtolerance");
                                        const underCol = findCol((n) => n.replace(/\s+/g, "") === "undertolerance");
                                        const showRangeCol = !!(lengthCol && (overCol || underCol));

                                        const computeRowRange = (g) => {
                                            if (!showRangeCol) return null;
                                            const lengthVal = parseFloat(lengthCol?.entries[g]?.char.value) || 0;
                                            const noOfDrumVal = parseFloat(noOfDrumCol?.entries[g]?.char.value) || 0;
                                            const overEntry = overCol?.entries[g]?.char;
                                            const underEntry = underCol?.entries[g]?.char;
                                            const overPct = parseFloat(overEntry?.value) || 0;
                                            const underPct = parseFloat(underEntry?.value) || 0;
                                            const toleranceApplied = !!(overEntry?.tolerance_applied || underEntry?.tolerance_applied);
                                            // This row's own Qty — No. of Drum/Coil × Length — same
                                            // "qty" base handleSaveCharModal uses per group.
                                            const rowQty = noOfDrumVal * lengthVal;
                                            if (!lengthVal && !rowQty) return null;
                                            const { rangeText } = calculateToleranceRange({
                                                toleranceApplied,
                                                length: lengthVal,
                                                noOfDrum: noOfDrumVal,
                                                qty: rowQty,
                                                overPercent: overPct,
                                                underPercent: underPct
                                            });
                                            return rangeText;
                                        };

                                        const renderValueCell = (char, index) => (
                                            (isEditing === false) ? (
                                                showsToleranceCheckbox(char) ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <span style={{ color: '#334155' }}>
                                                            {char.value || "—"}
                                                        </span>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!char.tolerance_applied}
                                                            disabled
                                                            style={{ cursor: 'default', flexShrink: 0 }}
                                                            title="Tolerance applied to Range calculation"
                                                        />
                                                    </div>
                                                ) : (
                                                    <span style={{ paddingLeft: '4px', color: '#334155' }}>
                                                        {char.value || "—"}
                                                    </span>
                                                )
                                            ) : isTypeOfPacking(char.characteristic) ? (
                                                <select
                                                    className="form-control"
                                                    value={char.value || ""}
                                                    onChange={(e) => handleChange(index, e.target.value)}
                                                    style={styles.compactInput}
                                                >
                                                    <option value="">Select</option>
                                                    {packingOptions.map((opt) => (
                                                        <option key={opt.id} value={opt.name}>
                                                            {opt.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : showsToleranceCheckbox(char) ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        className="form-control"
                                                        value={char.value || ""}
                                                        onChange={(e) => handleChange(index, sanitizeNumeric(e.target.value))}
                                                        style={{ ...styles.compactInput, width: '90px', flexShrink: 0 }}
                                                        placeholder="Enter value"
                                                    />
                                                    <input
                                                        type="checkbox"
                                                        checked={!!char.tolerance_applied}
                                                        onChange={() => handleToggleTolerance(index)}
                                                        style={{ cursor: 'pointer', flexShrink: 0 }}
                                                        title="Apply tolerance to Range calculation"
                                                    />
                                                </div>
                                            ) : (
                                                (() => {
                                                    const inputType = resolveInputType(char);
                                                    return (
                                                        <input
                                                            type="text"
                                                            inputMode={inputType === "numeric" ? "decimal" : undefined}
                                                            className="form-control"
                                                            value={char.value || ""}
                                                            onChange={(e) => {
                                                                const raw = e.target.value;
                                                                if (inputType === "numeric") {
                                                                    handleChange(index, sanitizeNumeric(raw));
                                                                } else if (inputType === "text") {
                                                                    handleChange(index, sanitizeText(raw));
                                                                } else {
                                                                    handleChange(index, raw);
                                                                }
                                                            }}
                                                            style={styles.compactInput}
                                                            placeholder="Enter value"
                                                        />
                                                    );
                                                })()
                                            )
                                        );

                                        return Array.from({ length: groupCount }).map((_, g) => (
                                            <tr key={g}>
                                                {columns.map((col, cIdx) => {
                                                    const entry = col.entries[g];
                                                    return (
                                                        <td key={cIdx} style={{ ...styles.erpTd, ...getColStyle(col.characteristic) }}>
                                                            {entry
                                                                ? renderValueCell(entry.char, entry.index)
                                                                : <span style={{ color: '#cbd5e1' }}>—</span>}
                                                        </td>
                                                    );
                                                })}
                                                {showRangeCol && (
                                                    <td style={{ ...styles.erpTd, whiteSpace: 'nowrap' }}>
                                                        <div style={{ height: '30px', display: 'flex', alignItems: 'center' }}>
                                                            <span style={{ color: '#334155', fontWeight: 500 }}>
                                                                {computeRowRange(g) || "—"}
                                                            </span>
                                                        </div>
                                                    </td>
                                                )}
                                                {isEditing !== false && onRemove && (
                                                    <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                        <div style={{ height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => onRemove(g)}
                                                                disabled={groupCount <= 1}
                                                                title={groupCount <= 1 ? "At least one group is required" : "Remove this Drum/Coil group"}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: groupCount <= 1 ? 'not-allowed' : 'pointer',
                                                                    color: groupCount <= 1 ? '#cbd5e1' : '#ef4444',
                                                                    padding: 4,
                                                                    borderRadius: 4,
                                                                    display: 'flex',
                                                                    transition: 'background 0.15s'
                                                                }}
                                                                onMouseEnter={e => { if (groupCount > 1) e.currentTarget.style.background = '#fee2e2'; }}
                                                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ));
                                    })()
                                ) : (
                                    <tr>
                                        <td style={{ ...styles.erpTd, textAlign: 'center', color: '#94a3b8' }}>
                                            No characteristics available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.modalFooter}>
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleClose} style={{ fontSize: '13px' }}>
                        Close
                    </button>
                   
                </div>
            </div>
        </div>
    );
};

const styles = {
    modalBackdrop: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1070,
        padding: '20px'
    },
    modal: {
        background: '#fff',
        borderRadius: '8px',
        width: '980px',
        maxWidth: '95%',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    },
    modalHeader: {
        padding: '12px 20px',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#fff'
    },
    modalBody: {
        padding: '0',
        overflowY: 'auto',
        flex: 1,
        background: '#fff'
    },
    modalFooter: {
        padding: '12px 20px',
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        background: '#f8fafc'
    },
    iconBtn: {
        background: 'transparent',
        border: 'none',
        fontSize: '18px',
        cursor: 'pointer',
        color: '#64748b'
    },
    tableContainer: {
        padding: '0',
        overflowX: 'auto'
    },
    toleranceNote: {
        fontSize: '12px',
        color: '#64748b',
        padding: '8px 16px',
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0'
    },
    erpTh: {
        background: "#f8fafc",
        fontSize: "13px",
        fontWeight: 600,
        padding: "10px 16px",
        borderBottom: "1px solid #e2e8f0",
        color: "#1e293b",
        textAlign: "left",
        whiteSpace: "nowrap"
    },
    erpTd: {
        fontSize: "13px",
        padding: "8px 16px",
        borderBottom: "1px solid #f1f5f9",
        color: "#334155",
        verticalAlign: "top"
    },
    compactInput: {
        fontSize: "13px",
        height: "30px",
        padding: "2px 8px",
        borderRadius: "4px",
        borderColor: "#cbd5e1",
        width: "100%"
    }
};

CharacteristicsModal.propTypes = {
    modalVisible: PropTypes.bool.isRequired,
    selectedCharacteristics: PropTypes.arrayOf(
        PropTypes.shape({
            characteristic: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
    ),
    handleChange: PropTypes.func.isRequired,
    handleToggleTolerance: PropTypes.func,
    handleClose: PropTypes.func.isRequired,
    onAdd: PropTypes.func,
    onRemove: PropTypes.func,
    onSave: PropTypes.func,
    isEditing: PropTypes.bool,
    title: PropTypes.string
};

export default CharacteristicsModal;